import { memo, useEffect, useState } from 'react';
import styles from './ClientStyles.module.scss';

// Phone-side config editor. The editor seat is auto-assigned server-side (ConArtist pattern).
//
// Layout:
//   - Editor: three rows (Length / Players / Answers). Each row has a title with a small ⓘ
//     button that opens a modal explaining the dimension. Below the title is a pill button
//     for each option. The Start/Back buttons stack vertically at the bottom.
//   - Non-editor: a single centred message showing who is configuring.

// Values stay as the underlying enum strings so server validation + saved config keep working.
// Labels are display-only — change here without touching the wire format.
const OPTIONS = {
    gameType: [
        { value: 'quick', label: 'Short' },
        { value: 'long',  label: 'Long' },
    ],
    gameMode: [
        { value: 'individuals', label: 'Solo' },
        { value: 'teams',       label: 'Teams' },
    ],
    answerMode: [
        { value: 'fuzzy', label: 'Say It' },
        { value: 'exact', label: 'Type It' },
    ],
};

// Per-section help: short intro + a list of modes with their own description. Rendered
// inside the info modal opened by the row's ⓘ icon.
const SECTION_INFO = {
    gameType: {
        title: 'Length',
        intro: 'How long the round runs.',
        modes: [
            { name: 'Short', desc: 'Up to 13 phrases. Roughly 15 minutes.' },
            { name: 'Long',  desc: 'Up to 25 phrases. Roughly 30 minutes.' },
        ],
    },
    gameMode: {
        title: 'Players',
        intro: 'How scoring works.',
        modes: [
            { name: 'Solo',  desc: 'Every player scores for themselves.' },
            { name: 'Teams', desc: 'Players are grouped and share a team score. Needs at least 2 players.' },
        ],
    },
    answerMode: {
        title: 'Answers',
        intro: 'How players answer.',
        modes: [
            { name: 'Say It',  desc: 'Buzz in, then say or type your answer. Close matches count.' },
            { name: 'Type It', desc: 'Buzz in, then type each letter into slots on your phone. Spelling must be exact.' },
        ],
    },
};

const GameConfigEditor = memo(function GameConfigEditor({
    show,
    catchphraseData,
    isEditor,
    editorName,
    playerCount,
    onUpdate,
    onBack,
    onStart,
    onClaimEditor,
    renderTextDiv,
}) {
    // Always render so the container can animate out; .show class toggles enter/exit.
    const gameType = catchphraseData?.gameType || 'quick';
    const gameMode = catchphraseData?.gameMode || 'individuals';
    const answerMode = catchphraseData?.answerMode || 'fuzzy';
    const teamsBlocked = (playerCount ?? 0) < 2;
    const cannotStartTeams = gameMode === 'teams' && teamsBlocked;

    // Freeze the editor-vs-readonly choice while animating out. When the host commits the
    // config, the server clears configEditorId at the moment of transition — so isEditor flips
    // to false before the slide-out finishes, and without this snapshot you'd briefly see the
    // read-only "Name is choosing the settings" view animating away.
    const [renderedIsEditor, setRenderedIsEditor] = useState(isEditor);
    useEffect(() => {
        if (show) setRenderedIsEditor(isEditor);
    }, [isEditor, show]);

    // Which section's info modal is open ("gameType" / "gameMode" / "answerMode" / null).
    const [infoFor, setInfoFor] = useState(null);

    // Defensive auto-claim: if we're in GameConfig and nobody holds the editor seat, claim
    // it after a short jittered delay. Stops players getting stuck on "Choosing the settings…"
    // when the server's auto-assign failed (e.g., editor disconnected and no one took over).
    // The jitter keeps multiple players from racing — server's OpenGameConfig handler also
    // rejects duplicates so only one will win.
    const editorId = catchphraseData?.configEditorId || "";
    useEffect(() => {
        if (!show || isEditor || editorId) return;
        const delay = 300 + Math.random() * 700;
        const t = setTimeout(() => onClaimEditor?.(), delay);
        return () => clearTimeout(t);
    }, [show, isEditor, editorId, onClaimEditor]);

    const renderRow = (fieldKey, currentValue, disabledValues = []) => {
        const options = OPTIONS[fieldKey];
        const info = SECTION_INFO[fieldKey];
        return (
            <div className={styles.configRow}>
                <div className={styles.configRowHead}>
                    <div className={styles.configRowTitle}>
                        {renderTextDiv ? renderTextDiv(info.title) : info.title}
                    </div>
                    <button
                        type="button"
                        className={styles.configInfoButton}
                        aria-label={`About ${info.title}`}
                        onClick={() => setInfoFor(fieldKey)}
                    >
                        i
                    </button>
                </div>
                <div className={styles.configRowButtons}>
                    {options.map(opt => {
                        const isActive = currentValue === opt.value;
                        const isDisabled = !renderedIsEditor || disabledValues.includes(opt.value);
                        return (
                            <button
                                key={opt.value}
                                className={`${styles.mainButton} ${styles.smaller} ${isActive ? '' : styles.alt}`}
                                onClick={isDisabled ? undefined : () => onUpdate({ [fieldKey]: opt.value })}
                                disabled={isDisabled}
                            >
                                {renderTextDiv ? renderTextDiv(opt.label) : opt.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Title block — mirrors the host's "CHOOSE THE / GAME SETTINGS" so both screens read
    // the same way. Sits absolutely at the top of the container so the editor rows + Start
    // buttons keep their thumb-reachable bottom anchor.
    const renderTitle = () => (
        <div className={styles.configHeader}>
            <div className={styles.configHeaderSub}>
                {renderTextDiv ? renderTextDiv('CHOOSE THE') : 'CHOOSE THE'}
            </div>
            <div className={styles.configHeaderMain}>
                {renderTextDiv ? renderTextDiv(<>GAME<br />SETTINGS</>) : 'GAME SETTINGS'}
            </div>
        </div>
    );

    // Non-editor view — single centred message naming whoever is configuring. No values,
    // no descriptions, no rows.
    if (!renderedIsEditor) {
        return (
            <div className={`${styles.configContainer} ${show ? styles.show : ''}`}>
                {renderTitle()}
                <div className={styles.configWaitingMessage}>
                    {editorName
                        ? `${editorName} is configuring the game…`
                        : 'Choosing the settings…'}
                </div>
            </div>
        );
    }

    const activeInfo = infoFor ? SECTION_INFO[infoFor] : null;

    return (
        <div className={`${styles.configContainer} ${show ? styles.show : ''}`}>
            {renderTitle()}
            <div className={styles.configRows}>
                {renderRow('gameType',   gameType)}
                {renderRow('gameMode',   gameMode, teamsBlocked ? ['teams'] : [])}
                {renderRow('answerMode', answerMode)}
            </div>

            <div className={styles.configBottomBar}>
                <div className={styles.configActionStack}>
                    <button
                        className={`${styles.mainButton} ${styles.smaller} ${styles.configActionStart}`}
                        onClick={cannotStartTeams ? undefined : onStart}
                        disabled={cannotStartTeams}
                    >
                        {renderTextDiv ? renderTextDiv('Start Game') : 'Start Game'}
                    </button>
                    <button
                        className={`${styles.mainButton} ${styles.smaller} ${styles.grey} ${styles.configActionBack}`}
                        onClick={onBack}
                    >
                        {renderTextDiv ? renderTextDiv('Back') : 'Back'}
                    </button>
                </div>
                {cannotStartTeams && (
                    <div className={styles.configHint}>Need at least 2 players to start a Teams game</div>
                )}
            </div>

            {activeInfo && (
                <div className={styles.configInfoOverlay} onClick={() => setInfoFor(null)}>
                    <div className={styles.configInfoPanel} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.configInfoTitle}>
                            {renderTextDiv ? renderTextDiv(activeInfo.title) : activeInfo.title}
                        </div>
                        <div className={styles.configInfoBody}>
                            <div className={styles.configInfoIntro}>{activeInfo.intro}</div>
                            <ul className={styles.configInfoModes}>
                                {activeInfo.modes.map(mode => (
                                    <li key={mode.name}>
                                        <span className={styles.configInfoModeName}>{mode.name}.</span>{' '}
                                        {mode.desc}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <button
                            type="button"
                            className={`${styles.mainButton} ${styles.smaller} ${styles.grey}`}
                            onClick={() => setInfoFor(null)}
                        >
                            {renderTextDiv ? renderTextDiv('Got it') : 'Got it'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
});

export default GameConfigEditor;
